import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Divider, Button, TextField,
    CircularProgress, Card, CardContent, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Avatar, Chip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
    Person, SupportAgent, SmartToy, LocationOn, AttachMoney, DriveEta
} from '@mui/icons-material';
import { getSupportTicket, getTicketMessages, sendTicketMessage, updateSupportTicket } from '../services/supportService';
import { socketService } from '../services/socketService';
import { getAdminUidFromToken } from '../utils/auth';
import { SupportTicket, ChatMessage } from '../types/Support';

const SupportTicketDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<SupportTicket | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);

    // Status Update States
    const [pendingStatus, setPendingStatus] = useState<string>('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const ticketId = Number(id);
    const isChatActive = ticket?.status === 'open';

    const currentAdminUid = useMemo(() => getAdminUidFromToken(), []);

    // 1. Initial Data Load (HTTP)
    const loadData = async () => {
        try {
            const [ticketRes, msgRes] = await Promise.all([
                getSupportTicket(ticketId),
                getTicketMessages(ticketId)
            ]);
            setTicket(ticketRes.data);
            setPendingStatus(ticketRes.data.status);
            // The API returns Newest First (Desc). We sort Ascending (Oldest First)
            // so they appear top-to-bottom correctly.
            const sortedMessages = msgRes.data.sort((a, b) =>
                new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
            );
            setMessages(sortedMessages);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ticketId) loadData();
    }, [ticketId]);

    // 2. Socket.IO Integration (Real-time)
    useEffect(() => {
        if (!ticket?.chatRoomId) return;

        // A. Connect & Join
        socketService.connect();
        socketService.joinRoom(ticket.chatRoomId);

        // B. Listen for incoming messages
        socketService.onMessageReceived((newMsg: ChatMessage) => {
            // Prevent duplicates if API load and Socket event overlap
            setMessages((prev) => {
                // If message ID already exists, ignore
                if (prev.some(m => m.id === newMsg.id)) return prev;
                // Append to bottom (Oldest -> Newest flow)
                return [...prev, newMsg];
            });
        });

        // C. Cleanup on Unmount
        return () => {
            if (ticket.chatRoomId) {
                socketService.leaveRoom(ticket.chatRoomId);
            }
            socketService.offMessageReceived();
        };
    }, [ticket?.chatRoomId]);


    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !isChatActive) return;
        try {
            // 1. Send via API (This pushes to Redis Backend)
            await sendTicketMessage(ticketId, newMessage);

            // 2. Optimistic Update not strictly needed if Socket is fast,
            // but if you want it, ensure it's added to the END of the array.
            setNewMessage('');
        } catch (err) {
            console.error("Failed to send", err);
        }
    };


    const handleStatusSelect = (newStatus: string) => setPendingStatus(newStatus);
    const handleUpdateClick = () => { if (ticket && pendingStatus !== ticket.status) setConfirmOpen(true); };
    const handleConfirmUpdate = async () => {
        if (!ticket) return;
        try {
            await updateSupportTicket(ticket.id, { status: pendingStatus as any });
            setTicket({ ...ticket, status: pendingStatus as any });
            setConfirmOpen(false);
        } catch (err) {
            console.error("Failed to update status", err);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (!ticket) return <Box sx={{ p: 3 }}><Typography>Ticket not found</Typography></Box>;

    // Helper for visual rows in the context panel
    const ContextRow = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: React.ReactNode }) => (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary">{label}</Typography>
            </Box>
            <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
        </Box>
    );

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', p: 2, maxWidth: 1600, mx: 'auto', width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/support-tickets')} sx={{ mb: 2, alignSelf: 'flex-start' }}>
                Back to Ticket List
            </Button>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, flexGrow: 1, height: '100%', overflow: 'hidden' }}>

                {/* LEFT PANEL */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: 0, height: { xs: 'auto', md: '100%' }, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                Ticket #{ticket.id}
                                <Chip label={ticket.status} color={ticket.status === 'open' ? 'error' : ticket.status === 'resolved' ? 'success' : 'warning'} size="small" variant="filled" />
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Update Status</InputLabel>
                                    <Select value={pendingStatus} label="Update Status" onChange={(e) => handleStatusSelect(e.target.value)}>
                                        <MenuItem value="open">Open</MenuItem>
                                        <MenuItem value="in_progress">In Progress</MenuItem>
                                        <MenuItem value="resolved">Resolved</MenuItem>
                                        <MenuItem value="closed">Closed</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button variant="contained" color="primary" disabled={pendingStatus === ticket.status} onClick={handleUpdateClick} sx={{ minWidth: 50, height: 40 }}>
                                    <SaveIcon fontSize="small" />
                                </Button>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <ContextRow icon={<SupportAgent fontSize="small" color="action"/>} label="Issue Type" value={<Chip label={ticket.issueType} size="small" />} />
                            <ContextRow icon={<Person fontSize="small" color="action"/>} label="Created By UID" value={ticket.createdByUid.substring(0, 10) + "..."} />
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px dashed #bdbdbd' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>DESCRIPTION</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>"{ticket.description}"</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                    {ticket.chatRoom?.ride && (
                        <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #1976d2' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DriveEta color="primary" /> Related Ride
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <ContextRow icon={<LocationOn fontSize="small" color="error"/>} label="Pickup" value={ticket.chatRoom.ride.pickupLocation} />
                                <ContextRow icon={<LocationOn fontSize="small" color="success"/>} label="Dropoff" value={ticket.chatRoom.ride.dropoffLocation} />
                                <ContextRow icon={<AttachMoney fontSize="small" color="action"/>} label="Fare" value={`₹${ticket.chatRoom.ride.totalFare}`} />
                                <Button variant="outlined" fullWidth size="small" sx={{ mt: 2 }} onClick={() => navigate(`/ride/${ticket.chatRoom?.rideId}`)}>View Full Ride Details</Button>
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {/* RIGHT PANEL: Chat */}
                <Box sx={{ flex: { xs: 'none', md: 2 }, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Ticket Discussion</Typography>
                            <Typography variant="caption" color="textSecondary">Ticket ID: #{ticket.id} • Room ID: {ticket.chatRoomId}</Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {messages.map((msg) => {
                                // Logic: If senderUid matches "Me" (Admin), it goes Right.
                                // Else it goes Left.
                                const isMe = msg.senderUid === currentAdminUid || msg.senderUid === 'ADMIN_SUPPORT';
                                const isBot = msg.isBot;

                                // Color logic
                                const bubbleColor = isMe ? '#1976d2' : isBot ? '#eeeeee' : '#ffffff';
                                const textColor = isMe ? '#fff' : '#000';
                                const align = isMe ? 'flex-end' : isBot ? 'center' : 'flex-start';

                                return (
                                    <Box key={msg.id} sx={{ display: 'flex', justifyContent: align, alignItems: 'flex-end', gap: 1 }}>
                                        {/* Left Avatar (User) */}
                                        {!isMe && !isBot && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#9c27b0' }}><Person fontSize="small" /></Avatar>
                                        )}
                                        {isBot && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#757575' }}><SmartToy fontSize="small" /></Avatar>
                                        )}

                                        <Paper elevation={1} sx={{
                                            p: 1.5, maxWidth: '70%', bgcolor: bubbleColor, color: textColor,
                                            borderRadius: isMe ? '20px 20px 0 20px' : isBot ? '20px' : '20px 20px 20px 0',
                                            position: 'relative'
                                        }}>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{msg.content}</Typography>
                                            <Typography variant="caption" sx={{
                                                display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.7rem',
                                                color: isMe ? '#e3f2fd' : 'text.secondary'
                                            }}>
                                                {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </Paper>

                                        {/* Right Avatar (Admin) */}
                                        {isMe && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}><SupportAgent fontSize="small" /></Avatar>
                                        )}
                                    </Box>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </Box>

                        <Box sx={{ p: 2, bgcolor: '#fff', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                            {isChatActive ? (
                                <>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Type a reply..."
                                        size="small"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                    <Button
                                        variant="contained"
                                        endIcon={<SendIcon />}
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim()}
                                        sx={{ borderRadius: 3, px: 3 }}
                                    >
                                        Send
                                    </Button>
                                </>
                            ) : (
                                <Box sx={{ width: '100%', p: 1, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        This ticket is <strong>{ticket?.status}</strong>. Chat is disabled.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* Dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Status Update</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change status to <strong>{pendingStatus}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmOpen(false); setPendingStatus(ticket.status); }} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmUpdate} color="primary" variant="contained" autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupportTicketDetail;